import React, { useState } from "react";
import { Helmet } from "react-helmet";
import "./ServiceBooking.css";
import db from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

const ServiceBooking = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    budget: "",
    contactMethod: "",
    timeline: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const siteUrl = "https://www.samsoftix.com";
  const pageUrl = `${siteUrl}/serviceBooking`;

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setSuccess(false);

    try {
      await addDoc(collection(db, "bookings"), {
        ...formData,
        status: "new",
        createdAt: serverTimestamp(),
      });

      setFormData({
        name: "",
        email: "",
        phone: "",
        service: "",
        budget: "",
        contactMethod: "",
        timeline: "",
        message: "",
      });

      setSuccess(true);

      setTimeout(() => {
        setSuccess(false);
      }, 5000);
    } catch (error) {
      console.error("Error submitting booking:", error);

      alert(
        "❌ There was an error submitting your request. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": [
          "LocalBusiness",
          "ProfessionalService",
        ],
        "@id": `${siteUrl}/#business`,
        name: "SamSoftix",
        url: siteUrl,
        telephone: "+92-332-0472184",
        email: "info@samsoftix.com",
        description:
          "SamSoftix is a software house and IT training center providing website development, mobile app development, ecommerce, custom software, AI solutions, SEO, digital marketing and technical services.",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Wan",
          addressRegion: "Punjab",
          postalCode: "51010",
          addressCountry: "PK",
        },
        areaServed: [
          {
            "@type": "City",
            name: "Wan",
          },
          {
            "@type": "City",
            name: "Sambrial",
          },
          {
            "@type": "City",
            name: "Sialkot",
          },
          {
            "@type": "AdministrativeArea",
            name: "Punjab",
          },
          {
            "@type": "Country",
            name: "Pakistan",
          },
          {
            "@type": "Country",
            name: "United Arab Emirates",
          },
          {
            "@type": "Country",
            name: "Saudi Arabia",
          },
          {
            "@type": "Country",
            name: "United States",
          },
          {
            "@type": "Country",
            name: "United Kingdom",
          },
        ],
        serviceType: [
          "Website Development",
          "Web Application Development",
          "Mobile App Development",
          "Ecommerce Development",
          "Custom Software Development",
          "AI Solutions",
          "SEO Services",
          "Digital Marketing",
          "UI/UX Design",
          "Cloud and Deployment Services",
          "IT Support",
        ],
        sameAs: [
          "https://www.facebook.com/samsoftix",
          "https://www.linkedin.com/company/samsoftix",
          "https://www.instagram.com/samsoftix",
        ],
      },

      {
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: "Book IT & Software Services | SamSoftix",
        description:
          "Request website development, mobile app development, ecommerce, custom software, AI, SEO, digital marketing, UI/UX and IT services from SamSoftix.",
        isPartOf: {
          "@id": `${siteUrl}/#website`,
        },
        about: {
          "@id": `${siteUrl}/#business`,
        },
        inLanguage: "en-PK",
      },

      {
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumb`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: siteUrl,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Service Booking",
            item: pageUrl,
          },
        ],
      },

      {
        "@type": "Service",
        name: "IT & Software Service Consultation",
        provider: {
          "@id": `${siteUrl}/#business`,
        },
        serviceType: "IT and Software Services",
        areaServed: [
          "Wan",
          "Sambrial",
          "Sialkot",
          "Pakistan",
          "United Arab Emirates",
          "Saudi Arabia",
          "United States",
          "United Kingdom",
        ],
        url: pageUrl,
      },
    ],
  };

  return (
    <div className="booking-page">
      <Helmet>
        {/* =========================
            BASIC SEO
        ========================== */}

        <html lang="en-PK" />

        <title>
          Book Website, App & IT Services | SamSoftix Pakistan
        </title>

        <meta
          name="description"
          content="Book website development, mobile app development, ecommerce, custom software, AI, SEO, UI/UX, digital marketing and IT services from SamSoftix in Sambrial, Sialkot and across Pakistan. Remote services are available internationally."
        />

        <meta
          name="keywords"
          content="
            SamSoftix service booking,
            book software development services,
            website development Pakistan,
            website development Sambrial,
            website development Sialkot,
            software house Sambrial,
            software company Sialkot,
            web development Pakistan,
            web application development Pakistan,
            React development Pakistan,
            Next.js development Pakistan,
            mobile app development Pakistan,
            Flutter app development Pakistan,
            React Native development Pakistan,
            ecommerce development Pakistan,
            Shopify development Pakistan,
            WooCommerce development Pakistan,
            custom software development Pakistan,
            AI development Pakistan,
            AI automation services Pakistan,
            SEO services Pakistan,
            local SEO Sambrial,
            local SEO Sialkot,
            digital marketing Pakistan,
            UI UX design Pakistan,
            website maintenance Pakistan,
            hosting setup Pakistan,
            domain setup Pakistan,
            cloud deployment Pakistan,
            IT support Pakistan,
            business software Pakistan,
            CRM development Pakistan,
            school software Pakistan,
            software solutions for businesses,
            remote software development,
            international software development
          "
        />

        <meta
          name="author"
          content="SamSoftix"
        />

        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />

        <meta
          name="googlebot"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />

        {/* =========================
            CANONICAL
        ========================== */}

        <link
          rel="canonical"
          href={pageUrl}
        />

        {/* =========================
            GEO SEO
        ========================== */}

        <meta
          name="geo.region"
          content="PK-PB"
        />

        <meta
          name="geo.placename"
          content="Wan, Sambrial, Punjab, Pakistan"
        />

        <meta
          name="geo.position"
          content="32.4496;74.3533"
        />

        <meta
          name="ICBM"
          content="32.4496, 74.3533"
        />

        {/* =========================
            OPEN GRAPH
        ========================== */}

        <meta
          property="og:type"
          content="website"
        />

        <meta
          property="og:title"
          content="Book Website, App & IT Services | SamSoftix"
        />

        <meta
          property="og:description"
          content="Request professional website development, mobile apps, ecommerce, custom software, AI, SEO, UI/UX and IT services from SamSoftix."
        />

        <meta
          property="og:url"
          content={pageUrl}
        />

        <meta
          property="og:site_name"
          content="SamSoftix"
        />

        <meta
          property="og:locale"
          content="en_PK"
        />

        {/* =========================
            TWITTER
        ========================== */}

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="Book IT & Software Services | SamSoftix"
        />

        <meta
          name="twitter:description"
          content="Book website development, mobile apps, ecommerce, AI, SEO, custom software and IT services from SamSoftix."
        />

        {/* =========================
            THEME
        ========================== */}

        <meta
          name="theme-color"
          content="#06111f"
        />

        {/* =========================
            STRUCTURED DATA
        ========================== */}

        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* =====================================================
          HERO
      ====================================================== */}

      <section className="booking-hero">
        <div className="booking-container">
          <span className="booking-kicker">
            SAMSOFTIX IT & SOFTWARE SERVICES
          </span>

          <h1>
            Book Website, App & IT Services
          </h1>

          <p>
            Tell us about your project, business or technical
            requirement. Our team will review your request and
            contact you to discuss the right solution.
          </p>

          <div className="booking-location-tags">
            <span>Wan</span>
            <span>Sambrial</span>
            <span>Sialkot</span>
            <span>Pakistan</span>
            <span>Remote Worldwide</span>
          </div>
        </div>
      </section>

      {/* =====================================================
          SUCCESS MESSAGE
      ====================================================== */}

      {success && (
        <div className="booking-success-wrapper">
          <p className="success-message">
            🎉 Your service request has been submitted
            successfully! Our team will contact you soon.
          </p>
        </div>
      )}

      {/* =====================================================
          BOOKING AREA
      ====================================================== */}

      <section className="booking-section">
        <div className="booking-container booking-layout">

          {/* FORM */}

          <div className="booking-form-wrapper">
            <div className="booking-form-heading">
              <span className="booking-kicker">
                PROJECT REQUEST
              </span>

              <h2>
                Tell Us About Your Project
              </h2>

              <p>
                Provide a few details so we can understand
                your requirements before contacting you.
              </p>
            </div>

            <form
              className="booking-form"
              onSubmit={handleSubmit}
            >
              <div className="booking-form-grid">

                {/* NAME */}

                <div className="booking-field">
                  <label htmlFor="name">
                    Full Name *
                  </label>

                  <input
                    id="name"
                    type="text"
                    name="name"
                    placeholder="Your Full Name"
                    value={formData.name}
                    onChange={handleChange}
                    autoComplete="name"
                    minLength="2"
                    maxLength="80"
                    required
                  />
                </div>

                {/* EMAIL */}

                <div className="booking-field">
                  <label htmlFor="email">
                    Email Address *
                  </label>

                  <input
                    id="email"
                    type="email"
                    name="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    autoComplete="email"
                    required
                  />
                </div>

                {/* PHONE */}

                <div className="booking-field">
                  <label htmlFor="phone">
                    Phone / WhatsApp *
                  </label>

                  <input
                    id="phone"
                    type="tel"
                    name="phone"
                    placeholder="+92 332 0472184"
                    value={formData.phone}
                    onChange={handleChange}
                    autoComplete="tel"
                    required
                  />
                </div>

                {/* SERVICE */}

                <div className="booking-field">
                  <label htmlFor="service">
                    Required Service *
                  </label>

                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                  >
                    <option value="">
                      -- Select Service --
                    </option>

                    <option value="Website Development">
                      Website Development
                    </option>

                    <option value="Web Application Development">
                      Web Application Development
                    </option>

                    <option value="App Development">
                      Mobile App Development
                    </option>

                    <option value="E-commerce Solutions">
                      Ecommerce Solutions
                    </option>

                    <option value="Shopify Development">
                      Shopify Development
                    </option>

                    <option value="WooCommerce Development">
                      WooCommerce Development
                    </option>

                    <option value="System Modification">
                      System Modification
                    </option>

                    <option value="Management Systems">
                      Management Systems
                    </option>

                    <option value="Custom Software Development">
                      Custom Software Development
                    </option>

                    <option value="Web Hosting & Domain Management">
                      Web Hosting & Domain Management
                    </option>

                    <option value="App Deployment">
                      App Deployment
                    </option>

                    <option value="SEO & Optimization">
                      SEO & Website Optimization
                    </option>

                    <option value="Local SEO">
                      Local SEO
                    </option>

                    <option value="UI/UX Design">
                      UI/UX Design
                    </option>

                    <option value="API Integration">
                      API Integration
                    </option>

                    <option value="Cloud & DevOps">
                      Cloud & DevOps
                    </option>

                    <option value="AI & Automation">
                      AI & Automation
                    </option>

                    <option value="Technical Consultation">
                      Technical Consultation
                    </option>

                    <option value="Social Media Post Creation">
                      Social Media Post Creation
                    </option>

                    <option value="Logo Design">
                      Logo Design
                    </option>

                    <option value="Business Card Design">
                      Business Card Design
                    </option>

                    <option value="Social Media Page Management">
                      Social Media Page Management
                    </option>

                    <option value="Digital Marketing">
                      Digital Marketing
                    </option>

                    <option value="Chatbot Training">
                      Chatbot Training
                    </option>

                    <option value="Dataset Creation">
                      Dataset Creation
                    </option>

                    <option value="Debugging & Testing">
                      Debugging & Testing
                    </option>

                    <option value="Data Cleaning & Management">
                      Data Cleaning & Management
                    </option>

                    <option value="Lab Setup (Schools & Colleges)">
                      School & College Lab Setup
                    </option>

                    <option value="Hardware Maintenance">
                      Hardware Maintenance
                    </option>

                    <option value="Other">
                      Other
                    </option>
                  </select>
                </div>

                {/* BUDGET */}

                <div className="booking-field">
                  <label htmlFor="budget">
                    Estimated Budget
                  </label>

                  <div className="booking-input-prefix">
                    <span>USD</span>

                    <input
                      id="budget"
                      type="number"
                      name="budget"
                      placeholder="e.g. 500"
                      value={formData.budget}
                      onChange={handleChange}
                      min="1"
                      step="1"
                    />
                  </div>
                </div>

                {/* CONTACT METHOD */}

                <div className="booking-field">
                  <label htmlFor="contactMethod">
                    Preferred Contact Method
                  </label>

                  <select
                    id="contactMethod"
                    name="contactMethod"
                    value={formData.contactMethod}
                    onChange={handleChange}
                  >
                    <option value="">
                      Select Contact Method
                    </option>

                    <option value="Email">
                      Email
                    </option>

                    <option value="Phone Call">
                      Phone Call
                    </option>

                    <option value="WhatsApp">
                      WhatsApp
                    </option>

                    <option value="Zoom/Meet">
                      Zoom / Google Meet
                    </option>
                  </select>
                </div>

                {/* TIMELINE */}

                <div className="booking-field booking-field-full">
                  <label htmlFor="timeline">
                    Expected Timeline
                  </label>

                  <input
                    id="timeline"
                    type="text"
                    name="timeline"
                    placeholder="Example: 2 weeks, 1 month, ASAP"
                    value={formData.timeline}
                    onChange={handleChange}
                    maxLength="100"
                  />
                </div>

                {/* MESSAGE */}

                <div className="booking-field booking-field-full">
                  <label htmlFor="message">
                    Project Details
                  </label>

                  <textarea
                    id="message"
                    name="message"
                    placeholder="Tell us about your project, required features, target audience, existing website/app, or any other useful details."
                    value={formData.message}
                    onChange={handleChange}
                    rows="7"
                    maxLength="3000"
                  />
                </div>
              </div>

              {/* TERMS */}

              <label className="terms-conditions">
                <input
                  type="checkbox"
                  required
                />

                <span>
                  I agree to the{" "}
                  <a href="/terms">
                    Terms & Conditions
                  </a>{" "}
                  and understand that submitting this
                  form sends a service inquiry to SamSoftix.
                </span>
              </label>

              {/* SUBMIT */}

              <button
                type="submit"
                disabled={loading}
                className="booking-submit-btn"
              >
                {loading
                  ? "Submitting Request..."
                  : "Submit Service Request"}
              </button>
            </form>
          </div>

          {/* SIDE INFORMATION */}

          <aside className="booking-sidebar">

            <div className="booking-side-card">
              <span className="booking-side-icon">
                💡
              </span>

              <h2>
                What Happens Next?
              </h2>

              <ol>
                <li>
                  <strong>Submit your request</strong>
                  <span>
                    Tell us what you need and provide
                    your project details.
                  </span>
                </li>

                <li>
                  <strong>We review your requirements</strong>
                  <span>
                    Our team reviews the information
                    and identifies the appropriate
                    service approach.
                  </span>
                </li>

                <li>
                  <strong>We contact you</strong>
                  <span>
                    We discuss your requirements,
                    timeline and project scope.
                  </span>
                </li>

                <li>
                  <strong>Project planning</strong>
                  <span>
                    If you decide to proceed, we
                    discuss the next steps for your
                    project.
                  </span>
                </li>
              </ol>
            </div>

            <div className="booking-side-card">
              <span className="booking-side-icon">
                🌍
              </span>

              <h2>
                Serving Local & International Clients
              </h2>

              <p>
                SamSoftix provides software development
                and IT services from Pakistan for local
                businesses, educational institutions,
                startups and remote international clients.
              </p>

              <div className="booking-service-tags">
                <span>Wan</span>
                <span>Sambrial</span>
                <span>Sialkot</span>
                <span>Pakistan</span>
                <span>UAE</span>
                <span>Saudi Arabia</span>
                <span>USA</span>
                <span>UK</span>
              </div>
            </div>

          </aside>
        </div>
      </section>

      {/* =====================================================
          WHY SAMSOFTIX
      ====================================================== */}

      <section className="booking-info">
        <div className="booking-container">

          <div className="booking-info-heading">
            <span className="booking-kicker">
              WHY SAMSOFTIX
            </span>

            <h2>
              Technology Solutions Built Around Your Requirements
            </h2>

            <p>
              Whether you need a business website, mobile
              application, ecommerce store, custom software,
              AI solution, SEO support or technical
              assistance, you can use this form to start
              a conversation with our team.
            </p>
          </div>

          <div className="booking-benefits">

            <div className="booking-benefit">
              <span>💻</span>

              <h3>
                Business Websites
              </h3>

              <p>
                Responsive websites for businesses,
                startups, service providers and
                organizations.
              </p>
            </div>

            <div className="booking-benefit">
              <span>📱</span>

              <h3>
                Mobile Applications
              </h3>

              <p>
                Mobile app development for Android
                and iOS using modern technologies.
              </p>
            </div>

            <div className="booking-benefit">
              <span>🛒</span>

              <h3>
                Ecommerce
              </h3>

              <p>
                Ecommerce solutions using Shopify,
                WooCommerce or custom development.
              </p>
            </div>

            <div className="booking-benefit">
              <span>🤖</span>

              <h3>
                AI & Automation
              </h3>

              <p>
                Practical AI integrations and
                automation solutions for digital
                products and businesses.
              </p>
            </div>

            <div className="booking-benefit">
              <span>🔍</span>

              <h3>
                SEO & Optimization
              </h3>

              <p>
                Technical SEO, website optimization,
                local SEO and search visibility support.
              </p>
            </div>

            <div className="booking-benefit">
              <span>🛠️</span>

              <h3>
                IT Support
              </h3>

              <p>
                Website maintenance, deployment,
                troubleshooting and technical support.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* =====================================================
          FINAL CTA
      ====================================================== */}

      <section className="booking-final-cta">
        <div className="booking-container">

          <span className="booking-kicker">
            READY TO START?
          </span>

          <h2>
            Have a Project in Mind?
          </h2>

          <p>
            Submit your requirements and let us
            understand what you need.
          </p>

          <a
            href="https://wa.me/923320472184"
            target="_blank"
            rel="noopener noreferrer"
            className="booking-whatsapp-btn"
          >
            💬 Contact SamSoftix on WhatsApp
          </a>

          <div className="booking-contact-line">
            <a href="mailto:info@samsoftix.com">
              info@samsoftix.com
            </a>

            <span>•</span>

            <a href="tel:+923320472184">
              +92 332 0472184
            </a>
          </div>

        </div>
      </section>
    </div>
  );
};

export default ServiceBooking;